import { makeAutoObservable, runInAction } from 'mobx';

import { agent } from '../api/agent';
import { toast } from 'react-toastify';
import i18n from '../../i18n';
import { TeamPostDto, TeamPostReplyDto, TeamReactionType } from '../models/Team';
 
// ── TeamPostStore ──────────────────────────────────────────────────────────────
 
export default class TeamPostStore {
  posts: TeamPostDto[] = [];
  loading = false;
  submitting = false;
  expandedReplies = new Set<string>(); // post IDs with expanded replies
 
  constructor() { makeAutoObservable(this); }

  // ── UTC helper ───────────────────────────────────────────────────────────────

  /**
   * Ensures a date string from the server is parsed as UTC.
   * API responses often omit the 'Z' suffix, which causes JS to treat
   * the value as local time instead of UTC.
   */
  private parseUTCDate = (value: Date | string | null | undefined): Date => {
    if (!value) return new Date();
    if (value instanceof Date) return value;
    const str = value.toString();
    const utcStr = str.endsWith('Z') || str.includes('+') ? str : str + 'Z';
    return new Date(utcStr);
  };

  private processReply = (reply: TeamPostReplyDto): TeamPostReplyDto => {
    return {
      ...reply,
      createdAt: this.parseUTCDate(reply.createdAt)
    };
  };

  private processPost = (post: TeamPostDto): TeamPostDto => {
    return {
      ...post,
      createdAt: this.parseUTCDate(post.createdAt),
      replies: post.replies.map(this.processReply)
    };
  };

  // ── Loaders ──────────────────────────────────────────────────────────────────

  loadPosts = async (contextType: string, contextId: string) => {
    this.loading = true;
    try {
      const posts = await agent.TeamPosts.list(contextType, contextId);
      runInAction(() => {
        this.posts = posts.map(this.processPost);
        this.loading = false;
      });
    } catch (error) {
      console.error(error);
      runInAction(() => { this.loading = false; });
    }
  };
 
  createPost = async (
    contextType: string,
    contextId: string,
    content: string,
    visibility: string = 'MembersOnly'
  ): Promise<boolean> => {
    this.submitting = true;
    try {
      const post = await agent.TeamPosts.create({ contextType, contextId, content, visibility });
      runInAction(() => {
        this.posts = [this.processPost(post), ...this.posts];
        this.submitting = false;
      });
      return true;
    } catch (error) {
      console.error(error);
      runInAction(() => { this.submitting = false; });
      toast.error((i18n as any).t('teamChat.errors.postFailed'));
      return false;
    }
  };
 
  deletePost = async (postId: string): Promise<boolean> => {
    this.submitting = true;
    try {
      await agent.TeamPosts.delete(postId);
      runInAction(() => {
        this.posts = this.posts.map(p =>
          p.id === postId ? { ...p, isDeleted: true, content: '' } : p
        );
        this.submitting = false;
      });
      return true;
    } catch (error) {
      console.error(error);
      runInAction(() => { this.submitting = false; });
      return false;
    }
  };
 
  pinPost = async (postId: string, pin: boolean): Promise<boolean> => {
    try {
      await agent.TeamPosts.pin(postId, pin);
      runInAction(() => {
        this.posts = this.posts.map(p => ({
          ...p,
          isPinned: p.id === postId ? pin : (pin ? false : p.isPinned)
        }));
        // Re-sort: pinned first
        this.posts = [
          ...this.posts.filter(p => p.isPinned),
          ...this.posts.filter(p => !p.isPinned),
        ];
      });
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };
 
  addReply = async (postId: string, content: string): Promise<boolean> => {
    this.submitting = true;
    try {
      const reply = await agent.TeamPosts.addReply(postId, content);
      runInAction(() => {
        this.posts = this.posts.map(p =>
          p.id === postId
            ? { ...p, replies: [...p.replies, this.processReply(reply)], replyCount: p.replyCount + 1 }
            : p
        );
        this.submitting = false;
      });
      return true;
    } catch (error) {
      console.error(error);
      runInAction(() => { this.submitting = false; });
      return false;
    }
  };
 
  deleteReply = async (postId: string, replyId: string): Promise<boolean> => {
    try {
      await agent.TeamPosts.deleteReply(replyId);
      runInAction(() => {
        this.posts = this.posts.map(p =>
          p.id === postId
            ? {
                ...p,
                replies: p.replies.map(r =>
                  r.id === replyId ? { ...r, isDeleted: true, content: '' } : r
                ),
              }
            : p
        );
      });
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };
 
  reactToPost = async (postId: string, reactionType: TeamReactionType): Promise<void> => {
    try {
      await agent.TeamPosts.reactToPost(postId, reactionType);
      runInAction(() => {
        this.posts = this.posts.map(p => {
          if (p.id !== postId) return p;
          const current = p.currentUserReaction;
          let reactions = [...p.reactions];
 
          if (current === reactionType) {
            // Toggle off
            reactions = reactions.map(r =>
              r.type === reactionType ? { ...r, count: Math.max(0, r.count - 1) } : r
            ).filter(r => r.count > 0);
            return { ...p, reactions, currentUserReaction: null };
          } else {
            // Remove old reaction if any
            if (current) {
              reactions = reactions.map(r =>
                r.type === current ? { ...r, count: Math.max(0, r.count - 1) } : r
              ).filter(r => r.count > 0);
            }
            // Add new
            const existing = reactions.find(r => r.type === reactionType);
            if (existing) {
              reactions = reactions.map(r =>
                r.type === reactionType ? { ...r, count: r.count + 1 } : r
              );
            } else {
              reactions = [...reactions, { type: reactionType, count: 1 }];
            }
            return { ...p, reactions, currentUserReaction: reactionType };
          }
        });
      });
    } catch (error) {
      console.error(error);
    }
  };
 
  reactToReply = async (postId: string, replyId: string, reactionType: TeamReactionType): Promise<void> => {
    try {
      await agent.TeamPosts.reactToReply(replyId, reactionType);
      runInAction(() => {
        this.posts = this.posts.map(p => {
          if (p.id !== postId) return p;
          return {
            ...p,
            replies: p.replies.map(r => {
              if (r.id !== replyId) return r;
              const current = r.currentUserReaction;
              let reactions = [...r.reactions];
 
              if (current === reactionType) {
                reactions = reactions.map(rx =>
                  rx.type === reactionType ? { ...rx, count: Math.max(0, rx.count - 1) } : rx
                ).filter(rx => rx.count > 0);
                return { ...r, reactions, currentUserReaction: null };
              } else {
                if (current) {
                  reactions = reactions.map(rx =>
                    rx.type === current ? { ...rx, count: Math.max(0, rx.count - 1) } : rx
                  ).filter(rx => rx.count > 0);
                }
                const existing = reactions.find(rx => rx.type === reactionType);
                reactions = existing
                  ? reactions.map(rx => rx.type === reactionType ? { ...rx, count: rx.count + 1 } : rx)
                  : [...reactions, { type: reactionType, count: 1 }];
                return { ...r, reactions, currentUserReaction: reactionType };
              }
            })
          };
        });
      });
    } catch (error) {
      console.error(error);
    }
  };
 
  toggleReplies = (postId: string) => {
    if (this.expandedReplies.has(postId)) {
      this.expandedReplies.delete(postId);
    } else {
      this.expandedReplies.add(postId);
    }
  };
 
  clearPosts = () => { this.posts = []; };
}